// hooks/api/internVendor.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as internVendorService from "@/services/internVendor.service";
import {
  InternVendorCreateType,
  InternVendorParamsType,
  InternVendorType,
} from "@/services/internVendor.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const internVendorsKey = {
  all: ["internVendors"] as const,
  lists: (filters: InternVendorParamsType) =>
    [...internVendorsKey.all, "lists", filters] as const,
  detail: (id: string) => [...internVendorsKey.all, "detail", id] as const,
};

export const useInternVendorsQuery = (filters: InternVendorParamsType) =>
  useQuery({
    queryKey: internVendorsKey.lists(filters),
    queryFn: () => internVendorService.fetchAllInternVendors(filters),
    placeholderData: (prev) => prev,
  });

export const useInternVendorByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: internVendorsKey.detail(id),
    queryFn: () => internVendorService.fetchInternVendorById(id),
    enabled: enabled && !!id,
  });

export const useCreateInternVendorMutation = (
  filters: InternVendorParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation<InternVendorType, AxiosError, InternVendorCreateType>({
    mutationFn: (data) => internVendorService.createInternVendor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internVendorsKey.lists(filters),
      });
      notifySuccess("Internship vendor added successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to add internship vendor"
      ),
  });
};

export const useUpdateInternVendorMutation = (
  filters: InternVendorParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      _id,
      data,
    }: {
      _id: string;
      data: InternVendorCreateType;
    }) => internVendorService.updateInternVendor(_id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: internVendorsKey.lists(filters),
      });
      queryClient.invalidateQueries({
        queryKey: internVendorsKey.detail(variables._id),
      });
      notifySuccess("Internship vendor updated successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to update internship vendor"
      ),
  });
};

export const useDeleteInternVendorMutation = (
  filters: InternVendorParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (_id: string) => internVendorService.deleteInternVendor(_id),
    onSuccess: (_, _id) => {
      queryClient.invalidateQueries({
        queryKey: internVendorsKey.lists(filters),
      });
      queryClient.removeQueries({ queryKey: internVendorsKey.detail(_id) });
      notifySuccess("Internship vendor deleted successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to delete internship vendor"
      ),
  });
};
